import { emotionColor } from './render-engine';

export interface ShotArtSeed {
  title: string;
  emotion?: string;
  index: number;
  width: number;
  height: number;
}

export function hashString(text: string): number {
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export function seededUnit(seed: number, salt: number): number {
  return (hashString(`${seed}:${salt}`) % 10000) / 10000;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const value = hex.replace('#', '');
  return {
    r: Number.parseInt(value.slice(0, 2), 16),
    g: Number.parseInt(value.slice(2, 4), 16),
    b: Number.parseInt(value.slice(4, 6), 16),
  };
}

function lighten(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  const mix = (channel: number) => Math.round(channel + (255 - channel) * amount);
  return `rgb(${mix(r)},${mix(g)},${mix(b)})`;
}

export function generateShotSvg(seed: ShotArtSeed): string {
  const { width, height, index, title } = seed;
  const base = emotionColor(seed.emotion, index);
  const seedValue = hashString(title + index);
  const accent = lighten(base, 0.18 + seededUnit(seedValue, 1) * 0.25);
  const glow = lighten(base, 0.45);

  const circles = Array.from({ length: 7 }, (_, i) => {
    const cx = seededUnit(seedValue, i + 2) * width;
    const cy = seededUnit(seedValue, i + 9) * height;
    const r = (0.08 + seededUnit(seedValue, i + 16) * 0.28) * Math.min(width, height);
    const opacity = 0.05 + seededUnit(seedValue, i + 23) * 0.1;
    return `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="${r.toFixed(1)}" fill="${glow}" opacity="${opacity.toFixed(3)}"/>`;
  }).join('');

  const gradientId = `g-${seedValue.toString(36)}`;
  const filterId = `n-${seedValue.toString(36)}`;

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}">
  <defs>
    <radialGradient id="${gradientId}" cx="50%" cy="42%" r="75%">
      <stop offset="0%" stop-color="${accent}"/>
      <stop offset="60%" stop-color="${base}"/>
      <stop offset="100%" stop-color="#050507"/>
    </radialGradient>
    <filter id="${filterId}">
      <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="${seedValue % 1000}"/>
      <feColorMatrix type="saturate" values="0"/>
      <feComponentTransfer>
        <feFuncA type="table" tableValues="0 0.14"/>
      </feComponentTransfer>
    </filter>
  </defs>
  <rect width="${width}" height="${height}" fill="url(#${gradientId})"/>
  ${circles}
  <rect width="${width}" height="${height}" filter="url(#${filterId})"/>
</svg>`;
}

export function svgToDataUrl(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export function generateShotArtDataUrl(seed: ShotArtSeed): string {
  return svgToDataUrl(generateShotSvg(seed));
}
